import React, { useState } from "react";
import { Leaf, X, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function Footer() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/contact", form);
      toast.success(r.data?.message || "Message sent — we'll be in touch soon.");
      setForm({ name: "", email: "", message: "" });
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <footer className="mt-32 border-t border-[#D1C7B8]/60 bg-[#F1ECE4]">
      <div className="px-6 sm:px-12 lg:px-20 py-16 grid md:grid-cols-4 gap-12">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <Leaf className="w-5 h-5 text-[#C05C42]" />
            <span className="font-serif text-2xl text-[#2D322B]">Badi</span>
          </div>
          <p className="text-sm text-[#5C6359] max-w-md leading-relaxed">
            A membership for those tending the long arc of vitality — where Indigenous wisdom,
            herbal & plant medicine, and precision science meet around the same fire.
          </p>
        </div>
        <div>
          <p className="label-eyebrow mb-4">Explore</p>
          <ul className="space-y-2 text-sm text-[#2D322B]">
            <li><a href="/library" className="hover:text-[#C05C42]">Library</a></li>
            <li><a href="/practitioners" className="hover:text-[#C05C42]">Practitioners</a></li>
            <li><a href="/pricing" className="hover:text-[#C05C42]">Membership</a></li>
            <li>
              <button
                type="button"
                onClick={() => setOpen(true)}
                data-testid="footer-contact-btn"
                className="hover:text-[#C05C42] transition-colors"
              >
                Contact us
              </button>
            </li>
          </ul>
        </div>
        <div>
          <p className="label-eyebrow mb-4">Acknowledgements</p>
          <p className="text-xs text-[#5C6359] leading-relaxed">
            We honour the traditional knowledge keepers, healers, and elders whose teachings
            inform this work. Badi is not a substitute for medical care.
          </p>
        </div>
      </div>
      <div className="px-6 sm:px-12 lg:px-20 py-6 border-t border-[#D1C7B8]/60 text-xs text-[#5C6359] flex justify-between">
        <span>© {new Date().getFullYear()} Badi · <span className="italic">to make open or grow</span> (Gangulu)</span>
        <span>Tended with care</span>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-[#2D322B]/80 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="contact-modal"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-[#F9F6F0] rounded-3xl max-w-lg w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-[#D1C7B8] shrink-0">
              <div>
                <p className="label-eyebrow text-[#C05C42]">Get in touch</p>
                <h2 className="font-serif text-2xl text-[#2D322B] mt-1">Contact us</h2>
              </div>
              <button onClick={() => setOpen(false)} data-testid="contact-close" className="text-[#5C6359] hover:text-[#C05C42]">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#5C6359] block mb-1">Your name</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="contact-name"
                  className="w-full bg-transparent border-b border-[#D1C7B8] focus:border-[#C05C42] py-2 outline-none text-[#2D322B]"
                  placeholder="Jane Doe"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#5C6359] block mb-1">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="contact-email"
                  className="w-full bg-transparent border-b border-[#D1C7B8] focus:border-[#C05C42] py-2 outline-none text-[#2D322B]"
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#5C6359] block mb-1">Message</label>
                <textarea
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="contact-message"
                  className="w-full bg-transparent border border-[#D1C7B8] focus:border-[#C05C42] rounded-xl p-3 outline-none text-[#2D322B]"
                  placeholder="How can we help?"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setOpen(false)} className="btn-secondary !py-2 !px-5">Cancel</button>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit"
                  className="btn-primary !py-2 !px-5 inline-flex items-center gap-2 disabled:opacity-60"
                >
                  <Send className="w-4 h-4" /> {sending ? "Sending…" : "Send message"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </footer>
  );
}
